import React, { useEffect, useState, useRef } from "react";
import { Range } from "react-range";
import { Box, Button } from "@mui/material";
import { useParams } from "react-router-dom";
import { toast } from "react-toastify";
import UploadedImage from "./UploadedImage";
import { useDispatch } from "react-redux";
import { AppDispatch } from "../../../store/store";
import { fetchImagesNew } from "../../../store/slice/ProjectImagesUploadedSlice";
import { openModal } from "../../../store/slice/UISlice";
import { UPLOAD_MODAL } from "../../../constants";

interface VideoUploadProps {
  video: File | null | undefined;
  onClose: () => void;
}

type Frame = {
  imgSrc: string;
  isChecked: boolean;
};

const VideoUpload: React.FC<VideoUploadProps> = ({ video, onClose }) => {
  interface Params extends Record<string, string | undefined> {
    id: string;
  }

  const dispatch: AppDispatch = useDispatch();
  const videoRef = useRef<HTMLVideoElement | null>(null);
  const video1Ref = useRef<HTMLVideoElement | null>(null);
  const intervalIdRef = useRef<NodeJS.Timeout | null>(null);

  const { id } = useParams<Params>();

  const [videoSrc, setVideoSrc] = useState("");
  const [frames, setFrames] = useState<Frame[]>([]);
  const [framesPerSecond, setFramesPerSecond] = useState([1]);
  const [uploadedFileName, setUploadedFileName] = useState("");
  const [isCapturing, setIsCapturing] = useState(false);
  const [leastFrameSelected, setLeastFrameSelected] = useState(false);
  const [isDataSetLoading, setIsDataSetloading] = useState(false);
  const [imageSrcs, setImageSrcs] = useState<string[]>([]);
  const [files, setFiles] = useState<File[]>([]);
  const [progress, setProgress] = useState(0);
  const [selectedFrames, setSelectedFrames] = useState(0);
  const [loading, setLoading] = useState(false);

  const count = 40;

  useEffect(() => {
    if (video) {
      setUploadedFileName(video.name);
      const videoURL = URL.createObjectURL(video);
      setVideoSrc(videoURL);
    }
    setFrames([]);
    setUploadedFileName("");
    setLeastFrameSelected(false);
  }, [video]);

  const onCapture = (frameData: string): void => {
    const frame: Frame = { imgSrc: frameData, isChecked: false };
    setFrames((prevFrames) => [...prevFrames, frame]);
  };

  const handleCapture = () => {
    if (!videoRef.current) return;

    setIsCapturing(true);
    setFrames([]);

    const video = videoRef.current;
    const videoTime = video.duration;

    const canvas = document.createElement("canvas");
    const context = canvas.getContext("2d");

    if (!context) return;

    let currentTime = 0;

    const captureFrames = (): void => {
      if (!videoRef.current) return;

      currentTime += 1 / framesPerSecond[0];

      if (currentTime > videoTime) {
        if (intervalIdRef.current) {
          clearInterval(intervalIdRef.current);
        }
        setIsCapturing(false);

        videoRef.current.pause();
        videoRef.current.currentTime = 0;
        return;
      }

      canvas.width = video.videoWidth;
      canvas.height = video.videoHeight;
      context.drawImage(video, 0, 0, canvas.width, canvas.height);
      const frameData = canvas.toDataURL("image/png");
      onCapture(frameData);
    };

    const interval = 1000 / framesPerSecond[0];
    intervalIdRef.current = setInterval(captureFrames, interval);
    video.play();
  };

  const getSelectedFrames = (index: number) => {
    const framesList = [...frames];
    framesList[index]["isChecked"] = !framesList[index]["isChecked"];

    let selectedFrames = framesList.filter((frame) => frame.isChecked === true);
    setSelectedFrames(selectedFrames.length);
    if (selectedFrames.length > 0) {
      setLeastFrameSelected(true);
    } else {
      setLeastFrameSelected(false);
    }

    setFrames(framesList);
  };

  const base64ToBlob = (base64: string, mimeType: string) => {
    setIsDataSetloading(true);
    const byteCharacters = atob(base64.split(",")[1]);
    const byteNumbers = new Array(byteCharacters.length);
    for (let i = 0; i < byteCharacters.length; i++) {
      byteNumbers[i] = byteCharacters.charCodeAt(i);
    }
    const byteArray = new Uint8Array(byteNumbers);
    return new Blob([byteArray], { type: mimeType });
  };

  //Upload the selected image to api
  const uploadToDataSet = () => {
    setLoading(true);
    console.log("hit here");
    setTimeout(() => {
      try {
        const formData = new FormData();
        if (id) {
          formData.append("ProjectId", id);
          formData.append("Id", "988");
        } else {
          setLoading(false);
          toast.error("ProjectId is undefined");
          return;
        }

        const newImageSrcs: string[] = [];
        const newFiles: File[] = [];

        let isValidImage = true;

        frames.map((frame, index) => {
          console.log("frames extraction");
          if (frame.isChecked) {
            const mimeType =
              frame.imgSrc?.split(",")[0].match(/:(.*?);/)?.[1] ?? "unknown";
            const blob = base64ToBlob(frame.imgSrc, mimeType);

            const fileName = `captured-frame${index}.png`;
            const file = new File([blob], fileName, { type: mimeType });
            if (file.size > 5 * 1024 * 1024) {
              isValidImage = false;
              return;
            }

            newFiles.push(file);
            newImageSrcs.push(frame.imgSrc);

            formData.append(
              "Images",
              blob,
              `image${index}.${mimeType.split("/")[1]}`
            );
          }
        });

        if (!isValidImage) {
          setLoading(false);

          toast.error("Images exceeded the 5 Mb");
          return;
        }

        const totalSize = newFiles.reduce((acc, file) => acc + file.size, 0);

        if (totalSize > 300 * 1024 * 1024) {
          setLoading(false);

          toast.error("Total file size exceeds the 300 MB limit!");
          return;
        }

        setImageSrcs(newImageSrcs);
        setFiles(newFiles);

        // Create a new XMLHttpRequest for uploading images
        const xhr = new XMLHttpRequest();
        xhr.open(
          "POST",
          `${process.env.REACT_APP_SERVER_BASEURL}/api/Images/Upload`
        );
        const token = localStorage.getItem("token");
        xhr.setRequestHeader("Authorization", `${token}`);

        xhr.upload.onprogress = (event) => {
          if (event.lengthComputable) {
            const percentComplete = (event.loaded / event.total) * 100;
            const onlyInteger = Math.floor(percentComplete);

            if (onlyInteger < 100) {
              setProgress(onlyInteger);
            }
          }
        };

        // Handle success
        xhr.onload = () => {
          if (xhr.status === 200) {
            setLoading(false);

            toast.success(
              `Image${
                JSON.parse(xhr.response).length === 1 ? "" : "s"
              } uploaded successfully!`
            );
            setProgress(100);

            dispatch(
              fetchImagesNew({
                projectId: id,
                page: 1,
                count: count,
                annotation: "all",
              })
            )
              .unwrap()
              .then((response) => {
                if (response.imageUrlModel.length === 0) {
                  dispatch(openModal({ name: UPLOAD_MODAL }));
                }
              });

            const parentElement = document.querySelector(".parent-class");
            if (parentElement) {
              parentElement.scrollTo({ top: 0, behavior: "smooth" });
            }

            onClose();
          } else {
            let errorMessage: string;

            if (typeof xhr.response === "string") {
              errorMessage = xhr.response;
            } else if (xhr.response && xhr.response.detail) {
              errorMessage = xhr.response.detail;
            } else {
              errorMessage = "Failed to upload images.";
            }

            console.error("Error Response:", xhr.response);
            toast.error(errorMessage);
            onClose();
          }
        };

        // Handle error
        xhr.onerror = () => {
          console.error("Error uploading images:", xhr.response);
          toast.error(xhr.response);
        };

        xhr.send(formData);
      } catch (e) {
        toast.error("failed");
      }
    }, 0);
  };

  const handleSelectAll = (event: React.ChangeEvent<HTMLInputElement>) => {
    const checked = event.target.checked;
    setFrames(frames.map((frame) => ({ ...frame, isChecked: checked })));
    if (checked) {
      setSelectedFrames(frames.length);
    } else {
      setSelectedFrames(0);
    }
    setLeastFrameSelected(checked);
  };

  useEffect(() => {
    return () => {
      URL.revokeObjectURL(videoSrc);
    };
  }, [videoSrc]);

  return (
    <div style={{ position: "relative" }}>
      {loading && imageSrcs.length === 0 && (
        <div
          style={{
            position: "absolute",
            textAlign: "center",
            justifyItems: "center",
            width: "100%",
            height: "100%",
            alignContent: "center",
            background: "white",
            zIndex: "99",
            borderRadius: "4px",
          }}
        >
          <span className="datasetloader"></span>
          <p>Adding to Dataset</p>
        </div>
      )}
      <h4 style={{ margin: "0" }}>
        {imageSrcs.length === 0 ? "Video Upload" : "Uploading Images"}
      </h4>
      {imageSrcs.length === 0 && (
        <>
          <div>
            {videoSrc && (
              <video width="0" controls ref={videoRef} muted>
                <source src={videoSrc} type="video/mp4" />
                Your browser does not support the video tag.
              </video>
            )}

            {videoSrc && !(frames.length > 0) && (
              <div style={{ marginBottom: "20px" }}>
                <video
                  autoPlay
                  muted
                  loop
                  width="300"
                  height="200"
                  ref={video1Ref}
                  style={{ width: "100%" }}
                >
                  <source src={videoSrc} type="video/mp4" />
                  Your browser does not support the video tag.
                </video>
              </div>
            )}
            <div
              style={{
                display: "flex",
                justifyContent:
                  !isCapturing && frames.length > 0 ? "space-between" : "end",
                marginBottom: "10px",
              }}
            >
              {!isCapturing && frames.length > 0 && (
                <div>
                  <label
                    style={{
                      alignItems: "center",
                      border: "1px solid grey",
                      padding: "10px 15px",
                      borderRadius: "25px",
                      height: "17px",
                      cursor: "pointer",
                      fontSize: "13px",
                      display: "flex",
                      gap: "6px",
                    }}
                    className="selectAll"
                  >
                    Select All ({selectedFrames}/{frames.length})
                    <input
                      id="selectAllImages"
                      type="checkbox"
                      checked={frames.every((frame) => frame.isChecked)}
                      onChange={handleSelectAll}
                    ></input>
                  </label>
                </div>
              )}
              <div
                style={{
                  display: "flex",
                  alignItems: "center",
                  gap: "20px",
                  justifyContent: "center",
                }}
              >
                <div
                  style={{
                    position: "relative",
                    width: "300px",
                    marginTop: "10px",
                    marginBottom: "20px",
                  }}
                  id="rangeAdjushment"
                >
                  <Range
                    step={1}
                    min={1}
                    max={30}
                    values={framesPerSecond}
                    onChange={(values) => setFramesPerSecond(values)}
                    renderTrack={({ props, children }) => (
                      <div
                        {...props}
                        style={{
                          ...props.style,
                          height: "6px",
                          width: "100%",
                          background: "grey",
                        }}
                      >
                        {children}
                      </div>
                    )}
                    renderThumb={({ props, isDragged }) => (
                      <div
                        {...props}
                        style={{
                          ...props.style,
                          height: "20px",
                          width: "20px",
                          borderRadius: "50%",
                          backgroundColor: isDragged ? "#1a73e8" : "#999",
                        }}
                      />
                    )}
                  />

                  {/* Tooltip */}
                  <div
                    style={{
                      position: "absolute",
                      top: "-30px",
                      left: `${(framesPerSecond[0] / 30) * 100}%`,
                      transform: "translateX(-50%)",
                      backgroundColor: "#333",
                      color: "#fff",
                      padding: "5px",
                      borderRadius: "3px",
                      fontSize: "14px",
                      width: "90px",
                      zIndex: "9",
                    }}
                  >
                    {framesPerSecond[0] + " frames/sec"}
                  </div>
                </div>
                <Button
                  style={{ marginBottom: "10px" }}
                  variant="contained"
                  onClick={handleCapture}
                  disabled={isCapturing}
                >
                  Capture Frames
                </Button>
              </div>
            </div>
          </div>
          <div
            style={{
              display: "grid",
              gap: "13px",
              gridTemplateColumns: "repeat(4,1fr)",
              height: frames.length > 0 ? "calc(100vh - 306px)" : "0",
              overflow: "scroll",
            }}
            id={frames.length > 0 ? "selectImagesMultiple" : ""}
          >
            {frames.map((frame, index) => {
              return (
                <div
                  style={{
                    position: "relative",
                    background: "#80808026",
                    height: "120px",
                    boxShadow: "0 0 4px #808080a8",
                    borderRadius: "4px",
                  }}
                  key={index}
                >
                  <label>
                    <img
                      key={frame.imgSrc}
                      src={frame.imgSrc}
                      alt={`frame-${index}`}
                      style={{
                        height: "100%",
                        width: "100%",
                        borderRadius: "4px",
                        objectFit: "contain",
                      }}
                    />
                    <input
                      key={index}
                      type="checkbox"
                      style={{ position: "absolute", top: "0", left: "0" }}
                      checked={frame.isChecked}
                      onChange={() => getSelectedFrames(index)}
                    ></input>
                  </label>
                </div>
              );
            })}
          </div>
          <div style={{ marginTop: "10px", textAlign: "right" }}>
            <Button onClick={onClose}>Cancel</Button>
            <Button
              id="uploadSelectedImages"
              variant="contained"
              onClick={uploadToDataSet}
              disabled={leastFrameSelected ? false : true}
            >
              Add to Dataset
            </Button>
          </div>
        </>
      )}
      {imageSrcs.length > 0 && (
        <Box
          sx={{
            border: "1px solid #E4E9F0",
            padding: "12px",
            borderRadius: "12px",
            width: "96%",
          }}
        >
          <UploadedImage
            imageUrl={imageSrcs}
            files={files}
            progress={progress}
          />
        </Box>
      )}
    </div>
  );
};

export default VideoUpload;
